import Dexie from 'dexie';

export class Database extends Dexie {
  constructor() {
    super('SamsunJeneratorDB');
    
    this.version(1).stores({
      products: '++id, name, brand, category, model, specifications, notes, image, cost, costCurrency, kdvPaid, profit, profitType, totalCost, netSalePrice, grossSalePrice, profitAmount, kdvCollected, netKdv, createdAt, updatedAt',
      settings: '++id, key, value'
    });
    
    this.products = this.table('products');
    this.settings = this.table('settings');
  }
}

export const db = new Database();

export const initializeSettings = async () => {
  const existingSettings = await db.settings.toArray();
  
  if (existingSettings.length === 0) {
    await db.settings.bulkAdd([
      { key: 'usdToTl', value: 34.50 },
      { key: 'eurToTl', value: 37.20 },
      { key: 'defaultKdvRate', value: 20 },
      { key: 'defaultProfitMargin', value: 20 },
      { key: 'defaultProfitType', value: 'percentage' }
    ]);
  }
};

export const getSettings = async () => {
  const settings = await db.settings.toArray();
  const settingsObj = {};
  settings.forEach(setting => {
    settingsObj[setting.key] = setting.value;
  });
  return settingsObj;
};

export const updateSetting = async (key, value) => {
  const existing = await db.settings.where('key').equals(key).first();
  if (existing) {
    await db.settings.update(existing.id, { value });
  } else {
    await db.settings.add({ key, value });
  }
};

export const calculateProductPricing = (product, settings) => {
  const baseCost = parseFloat(product.cost);
  const exchangeRate = product.costCurrency === 'USD' ? settings.usdToTl : 
                      product.costCurrency === 'EUR' ? settings.eurToTl : 1;
  
  // All calculations in original currency
  const kdvAmountPaid = baseCost * (product.kdvPaid / 100);
  const totalPurchaseCost = baseCost + kdvAmountPaid;
  
  // Calculate profit on NET COST (before KDV) in original currency
  let profitAmount = 0;
  if (product.profitType === 'percentage') {
    profitAmount = baseCost * (product.profit / 100);
  } else {
    // Convert fixed profit amount to original currency if needed
    profitAmount = product.costCurrency === 'TL' ? product.profit : product.profit / exchangeRate;
  }
  
  const netSalePrice = baseCost + profitAmount;
  const kdvCollected = netSalePrice * (settings.defaultKdvRate / 100);
  const grossSalePrice = netSalePrice + kdvCollected;
  const netKdv = kdvCollected - kdvAmountPaid;
  const profitMarginPercent = (profitAmount / baseCost) * 100;
  
  // Calculate TL equivalents
  const baseCostInTl = baseCost * exchangeRate;
  const kdvAmountPaidInTl = kdvAmountPaid * exchangeRate;
  const totalPurchaseCostInTl = totalPurchaseCost * exchangeRate;
  const profitAmountInTl = profitAmount * exchangeRate;
  const netSalePriceInTl = netSalePrice * exchangeRate;
  const kdvCollectedInTl = kdvCollected * exchangeRate;
  const grossSalePriceInTl = grossSalePrice * exchangeRate;
  const netKdvInTl = netKdv * exchangeRate;
  
  return {
    currency: product.costCurrency,
    exchangeRate: exchangeRate,
    baseCost: baseCost.toFixed(2),
    kdvAmount: kdvAmountPaid.toFixed(2),
    totalCost: totalPurchaseCost.toFixed(2),
    profitAmount: profitAmount.toFixed(2),
    netSalePrice: netSalePrice.toFixed(2),
    kdvCollected: kdvCollected.toFixed(2),
    grossSalePrice: grossSalePrice.toFixed(2),
    netKdv: netKdv.toFixed(2),
    profitMarginPercent: profitMarginPercent.toFixed(1),
    // TL equivalents
    baseCostInTl: baseCostInTl.toFixed(2),
    kdvAmountInTl: kdvAmountPaidInTl.toFixed(2),
    totalCostInTl: totalPurchaseCostInTl.toFixed(2),
    profitAmountInTl: profitAmountInTl.toFixed(2),
    netSalePriceInTl: netSalePriceInTl.toFixed(2),
    kdvCollectedInTl: kdvCollectedInTl.toFixed(2),
    grossSalePriceInTl: grossSalePriceInTl.toFixed(2),
    netKdvInTl: netKdvInTl.toFixed(2)
  };
};